/**
 * What this party has been billed — the list a shop opens their file to see.
 *
 * Rows come from the documents endpoint when the reader holds `invoices.read`
 * (which adds the due date, what was on the document and who raised it) and
 * fall back to the account statement otherwise, so a clerk without that
 * permission keeps the list they have today rather than losing the tab.
 *
 * Every row opens its paper: the printed invoice is the thing a customer
 * standing at the counter is asking for, and reaching it used to mean leaving
 * the customer's file for the invoices list and searching for the number.
 */
import type { PortalInvoiceSummary } from '@erp/shared';
import { Printer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { Button, EmptyState } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { appUrl } from '../../lib/basePath.js';
import { formatDate, formatNumber } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';
import { localToday } from '../documents/paymentState.js';

/** A document row as the invoices/bills endpoint returns it. */
interface DocRow {
  id: string;
  docNumber: string;
  docDate: string;
  dueDate?: string | null;
  status: string;
  currencyCode: string;
  total: string;
  amountPaid?: string;
  lineCount?: number;
  unitCount?: number;
  createdByName?: string | null;
}

/** The statement's summary, reshaped into the row above. */
function fromStatement(i: PortalInvoiceSummary): DocRow {
  return {
    id: i.id,
    docNumber: i.docNumber,
    docDate: i.docDate,
    dueDate: i.dueDate,
    status: i.status,
    currencyCode: i.currencyCode,
    total: i.total,
    amountPaid: i.amountPaid,
  };
}

export function PartyInvoicesTab({ partyId, fallback, loading, side = 'customer' }: {
  partyId: string;
  fallback: PortalInvoiceSummary[];
  loading: boolean;
  side?: 'customer' | 'vendor';
}): JSX.Element {
  const { t, i18n } = useTranslation('parties');
  const { t: td } = useTranslation('documents');
  const isVendor = side === 'vendor';
  const endpoint = isVendor ? '/bills' : '/invoices';
  const base = isVendor ? '/purchasing/bills' : '/sales/invoices';
  const canRead = usePermission(isVendor ? 'bills.read' : 'invoices.read');
  const [rows, setRows] = useState<DocRow[] | null>(null);

  useEffect(() => {
    if (!canRead) return undefined;
    let on = true;
    void api()
      .get<{ items: DocRow[] }>(endpoint, { params: { partyId, limit: 500 } })
      .then((r) => { if (on) setRows(r.data.items); })
      // Non-fatal: the statement rows below still render the history.
      .catch(() => { if (on) setRows(null); });
    return () => { on = false; };
  }, [partyId, endpoint, canRead]);

  const locale = i18n.language;
  const money = (a: string, c: string): string => formatMoney(a, { locale, currency: c });
  const list: DocRow[] = rows ?? fallback.map(fromStatement);
  // Only the endpoint rows carry what was ON the document; an always-empty
  // column reads as broken rather than as unavailable.
  const showItems = rows !== null && rows.some((r) => r.lineCount !== undefined);
  const today = localToday();

  if (loading && rows === null && fallback.length === 0) {
    return <Card className="p-4"><Skeleton className="h-40 w-full" /></Card>;
  }
  if (list.length === 0) {
    return <Card className="p-6"><EmptyState title={t('sales.empty', { defaultValue: 'No invoices yet.' })} /></Card>;
  }

  return (
    <Card className="overflow-x-auto p-0">
      <table className="w-full text-start text-sm">
        <thead className="border-b border-border bg-bg-subtle text-fg-muted">
          <tr>
            <th className="px-4 py-2.5 text-start">{t('sales.number', { defaultValue: 'Number' })}</th>
            <th className="px-4 py-2.5 text-start">{t('sales.date', { defaultValue: 'Date' })}</th>
            <th className="px-4 py-2.5 text-start">{t('account.due', { defaultValue: 'due' })}</th>
            {showItems && (
              <th className="px-4 py-2.5 text-end">
                {td('columnsShared.items', { defaultValue: 'Items' })}
              </th>
            )}
            <th className="px-4 py-2.5 text-start">{t('sales.status', { defaultValue: 'Status' })}</th>
            <th className="px-4 py-2.5 text-end">{t('sales.total', { defaultValue: 'Total' })}</th>
            <th className="px-4 py-2.5 text-end">{t('sales.paid', { defaultValue: 'Paid' })}</th>
            <th className="px-4 py-2.5 text-end">{t('sales.balance', { defaultValue: 'Balance' })}</th>
            <th className="w-12 px-4 py-2.5" />
          </tr>
        </thead>
        <tbody>
          {list.map((inv) => {
            const paid = inv.amountPaid ?? '0';
            // Cancelled: in no balance (its entry is reversed) — shown struck
            // through, owing nothing, never as an open amount.
            const cancelled = inv.status === 'cancelled';
            const balance = cancelled ? 0 : Number(inv.total) - Number(paid);
            const open = balance > 0.0001;
            // Past its due date with money still on it — the one row state that
            // names work to do, so it carries the only red on the table.
            const due = (inv.dueDate ?? '').slice(0, 10);
            const overdue = open && !!due && due < today && inv.status !== 'cancelled';
            return (
              <tr key={inv.id} className="border-b border-border/60 last:border-0 hover:bg-bg-subtle/50">
                <td className="px-4 py-2.5">
                  <Link to={`${base}/${inv.id}`} className="font-mono font-medium text-brand hover:underline">
                    {inv.docNumber}
                  </Link>
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap text-fg-muted">
                  {formatDate(inv.docDate, { locale })}
                </td>
                <td className={`px-4 py-2.5 whitespace-nowrap ${overdue ? 'font-semibold text-danger' : 'text-fg-muted'}`}>
                  {due ? formatDate(due, { locale }) : '—'}
                </td>
                {showItems && (
                  <td className="px-4 py-2.5 text-end font-mono tabular-nums text-xs text-fg-muted">
                    {inv.lineCount === undefined ? '—' : (
                      <span title={td('columnsShared.itemsHint', {
                        defaultValue: '{{lines}} products · {{units}} units',
                        lines: inv.lineCount,
                        units: inv.unitCount ?? 0,
                      })}
                      >
                        {inv.lineCount} <span className="text-fg-subtle">({formatNumber(inv.unitCount ?? 0, { locale })})</span>
                      </span>
                    )}
                  </td>
                )}
                <td className="px-4 py-2.5">
                  <Badge variant={overdue ? 'danger' : inv.status === 'paid' ? 'success' : inv.status === 'cancelled' ? 'secondary' : 'warning'}>
                    {overdue
                      ? td('columnsShared.pay_overdue', { defaultValue: 'Overdue' })
                      : t(`sales.statusValue.${inv.status}`, { defaultValue: inv.status })}
                  </Badge>
                </td>
                <td className={`px-4 py-2.5 text-end font-mono tabular-nums ${cancelled ? 'text-fg-subtle line-through' : ''}`}>{money(inv.total, inv.currencyCode)}</td>
                <td className="px-4 py-2.5 text-end font-mono tabular-nums text-fg-muted">{money(paid, inv.currencyCode)}</td>
                <td className={`px-4 py-2.5 text-end font-mono font-semibold tabular-nums ${cancelled ? 'text-fg-subtle' : open ? 'text-warning' : 'text-success'}`}>
                  {cancelled ? '—' : money(balance.toFixed(4), inv.currencyCode)}
                </td>
                <td className="px-4 py-2.5 text-end">
                  {/* window.open, not an <a target="_blank">: anchors get implicit
                      noopener and the print tab would lose the session. */}
                  <Button
                    variant="ghost"
                    size="sm"
                    title={t('sales.print', { defaultValue: 'Print invoice' })}
                    aria-label={`${t('sales.print', { defaultValue: 'Print invoice' })} ${inv.docNumber}`}
                    onClick={() => window.open(appUrl(`${base}/${inv.id}/print`), '_blank')}
                  >
                    <Printer className="h-4 w-4" aria-hidden />
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}
